import { useState } from 'react';
import { useDrag } from 'react-dnd';
import REGISTRY, { PALETTE_CATEGORIES, CATEGORY_ICONS } from '../blockRegistry';
import { ItemTypes } from './dndTypes';

function PaletteTile({ blockType }) {
  const def = REGISTRY[blockType];
  const itemType = def ? (def.kind === 'statement' ? ItemTypes.STATEMENT : ItemTypes.EXPRESSION) : ItemTypes.STATEMENT;

  const [{ isDragging }, drag] = useDrag(() => ({
    type: itemType,
    item: () => ({ origin: 'palette', blockType }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }), [blockType]);

  if (!def) return null;

  return (
    <div
      ref={drag}
      title={def.label}
      style={{
        padding: '5px 8px',
        marginBottom: '4px',
        backgroundColor: def.color,
        color: def.textColor || '#000',
        borderRadius: '4px',
        fontSize: '0.72vmax',
        fontFamily: 'Work Sans, sans-serif',
        fontWeight: 600,
        cursor: 'grab',
        userSelect: 'none',
        opacity: isDragging ? 0.4 : 1,
        transition: 'opacity 0.15s, box-shadow 0.15s',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${def.color}80`; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {def.shortLabel}
    </div>
  );
}

function Category({ cat, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{ marginBottom: '8px' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '4px 6px',
          backgroundColor: 'transparent',
          border: 'none',
          borderBottom: '1px solid #e5e7eb',
          color: '#374151',
          fontFamily: 'Work Sans, sans-serif',
          fontSize: '0.72vmax',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '6px',
        }}
      >
        <span>
          {CATEGORY_ICONS[cat.label] && (
            <span style={{ marginRight: '5px', opacity: 0.6 }}>{CATEGORY_ICONS[cat.label]}</span>
          )}
          {cat.label}
        </span>
        <span style={{ opacity: 0.5 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ paddingLeft: '4px' }}>
          {cat.types.map((t) => <PaletteTile key={t} blockType={t} />)}
        </div>
      )}
    </div>
  );
}

export default function BlockPalette({ searchTerm }) {
  const [search, setSearch] = useState(searchTerm || '');

  // Filter categories by search term
  const filtered = search.trim()
    ? PALETTE_CATEGORIES
        .map((cat) => ({
          ...cat,
          types: cat.types.filter((t) => {
            const def = REGISTRY[t];
            if (!def) return false;
            const q = search.toLowerCase();
            return (
              def.label.toLowerCase().includes(q) ||
              def.shortLabel.toLowerCase().includes(q) ||
              cat.label.toLowerCase().includes(q)
            );
          }),
        }))
        .filter((cat) => cat.types.length > 0)
    : PALETTE_CATEGORIES;

  return (
    <div style={{
      width: '160px',
      minWidth: '160px',
      flexShrink: 0,
      borderRight: '1px solid #e5e7eb',
      paddingRight: '10px',
      overflowY: 'auto',
      height: '100%',
    }}>
      <input
        type="text"
        placeholder="Search blocks..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: '100%',
          padding: '4px 8px',
          marginBottom: '10px',
          backgroundColor: '#ffffff',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          color: '#111827',
          fontSize: '0.75vmax',
          fontFamily: 'Work Sans, sans-serif',
          boxSizing: 'border-box',
        }}
      />
      {filtered.map((cat, i) => (
        <Category key={cat.label} cat={cat} defaultOpen={i < 2} />
      ))}
    </div>
  );
}
